/**
 * Step2Location Component
 *
 * Confirms location and selects utility providers:
 * - Confirm city jurisdiction (or select "Not in city limits")
 * - Confirm county jurisdiction
 * - Select electric utility provider
 * - Select gas utility provider (if applicable)
 */

import { useEffect } from 'react';

export default function Step2Location({ state, setField, errors }) {
  // Find the suggested city jurisdiction based on Google Places city
  const suggestedCityJurisdiction = state.availableJurisdictions.find(j =>
    j.jurisdiction_type === 'city' &&
    j.jurisdiction_name.toLowerCase() === state.propertyCity?.toLowerCase()
  );

  // Find the suggested county jurisdiction based on Google Places county
  // Try multiple matching strategies to handle different formats
  const suggestedCountyJurisdiction = state.availableJurisdictions.find(j => {
    if (j.jurisdiction_type !== 'county' || !state.propertyCounty) return false;

    const countyName = j.jurisdiction_name.toLowerCase();
    const searchCounty = state.propertyCounty.toLowerCase();

    // Strategy 1: Exact match
    if (countyName === searchCounty) return true;

    // Strategy 2: Match with "County" suffix added
    if (countyName === `${searchCounty} county`) return true;

    // Strategy 3: Match with "County" suffix removed from database name
    if (countyName.replace(/ county$/i, '') === searchCounty) return true;

    return false;
  });

  // Auto-select suggested jurisdictions when component mounts or suggestions change
  useEffect(() => {
    // Only auto-select if no jurisdictions are currently selected
    if (state.jurisdictionIds.length === 0) {
      const autoSelectedIds = [];

      // Auto-select suggested city if found
      if (suggestedCityJurisdiction) {
        autoSelectedIds.push(suggestedCityJurisdiction.id);
      }

      // Auto-select suggested county if found
      if (suggestedCountyJurisdiction) {
        autoSelectedIds.push(suggestedCountyJurisdiction.id);
      }

      // Update state if we found any suggestions
      if (autoSelectedIds.length > 0) {
        setField('jurisdictionIds', autoSelectedIds);
      }
    }
  }, [suggestedCityJurisdiction, suggestedCountyJurisdiction, state.jurisdictionIds.length, setField]);

  // Get all available city jurisdictions for dropdown
  const cityJurisdictions = state.availableJurisdictions.filter(j => j.jurisdiction_type === 'city');

  // Get all available county jurisdictions for dropdown
  const countyJurisdictions = state.availableJurisdictions.filter(j => j.jurisdiction_type === 'county');

  // Get currently selected city and county IDs
  const selectedCityId = state.jurisdictionIds.find(id =>
    state.availableJurisdictions.find(j => j.id === id && j.jurisdiction_type === 'city')
  );
  const selectedCountyId = state.jurisdictionIds.find(id =>
    state.availableJurisdictions.find(j => j.id === id && j.jurisdiction_type === 'county')
  );

  // Handle city selection
  const handleCityChange = (cityId) => {
    const newIds = state.jurisdictionIds.filter(id => {
      const j = state.availableJurisdictions.find(j => j.id === id);
      return j && j.jurisdiction_type !== 'city';
    });
    if (cityId) newIds.push(cityId);
    setField('jurisdictionIds', newIds);
  };

  // Handle county selection
  const handleCountyChange = (countyId) => {
    const newIds = state.jurisdictionIds.filter(id => {
      const j = state.availableJurisdictions.find(j => j.id === id);
      return j && j.jurisdiction_type !== 'county';
    });
    if (countyId) newIds.push(countyId);
    setField('jurisdictionIds', newIds);
  };

  return (
    <div className="card">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#2c3e50',
          marginBottom: '8px'
        }}>
          Step 2: Location & Utility Providers
        </h2>
        <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6' }}>
          Select the service area and utility providers for this property.
        </p>
      </div>

      {/* Property Address Summary */}
      <div style={{
        padding: '16px',
        background: '#f0f9ff',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '2px solid #bae6fd'
      }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e', marginBottom: '6px' }}>
          📍 Property Location
        </div>
        <div style={{ fontSize: '15px', color: '#0c4a6e' }}>
          {state.propertyStreet || 'No address entered'}<br/>
          {state.propertyCity && state.propertyState && state.propertyZip && (
            `${state.propertyCity}, ${state.propertyState} ${state.propertyZip}`
          )}
        </div>
      </div>

      {/* City Jurisdiction Selection */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          🏙️ City Jurisdiction
        </label>

        {suggestedCityJurisdiction && (
          <div style={{
            padding: '10px 12px',
            background: '#f0fdf4',
            border: '2px solid #22c55e',
            borderRadius: '6px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#14532d'
          }}>
            💡 Detected city: <strong>{state.propertyCity}</strong>
          </div>
        )}

        <select
          className="input"
          value={selectedCityId || ''}
          onChange={(e) => handleCityChange(e.target.value)}
        >
          <option value="">Not in city limits (County only)</option>
          {cityJurisdictions.map(city => (
            <option key={city.id} value={city.id}>
              {city.jurisdiction_name}
            </option>
          ))}
        </select>

        <div style={{
          fontSize: '12px',
          color: '#64748b',
          marginTop: '6px',
          lineHeight: '1.5'
        }}>
          Select a city if the property is within city limits, or choose "Not in city limits" for unincorporated areas.
        </div>
      </div>

      {/* County Jurisdiction Selection */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          🏘️ County Jurisdiction <span style={{ color: '#ef4444' }}>*</span>
        </label>

        {suggestedCountyJurisdiction && (
          <div style={{
            padding: '10px 12px',
            background: '#f0fdf4',
            border: '2px solid #22c55e',
            borderRadius: '6px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#14532d'
          }}>
            💡 Detected county: <strong>{state.propertyCounty} County</strong>
          </div>
        )}

        <select
          className="input"
          value={selectedCountyId || ''}
          onChange={(e) => handleCountyChange(e.target.value)}
          required
        >
          <option value="">-- Select County --</option>
          {countyJurisdictions.map(county => (
            <option key={county.id} value={county.id}>
              {county.jurisdiction_name}
            </option>
          ))}
        </select>

        {countyJurisdictions.length === 0 && (
          <div style={{
            fontSize: '13px',
            color: '#f59e0b',
            marginTop: '6px',
            padding: '8px',
            background: '#fffbeb',
            borderRadius: '4px'
          }}>
            ⚠️ No counties configured. Please add counties in Settings → Geography.
          </div>
        )}

        {errors.jurisdictionIds && (
          <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>
            {errors.jurisdictionIds}
          </div>
        )}
      </div>

      {/* Electric Utility Provider */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          ⚡ Electric Utility Provider <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <select
          className="input"
          value={state.electricProviderId || ''}
          onChange={(e) => setField('electricProviderId', e.target.value)}
          required
        >
          <option value="">-- Select Electric Provider --</option>
          {state.electricProviders.map(provider => (
            <option key={provider.id} value={provider.id}>
              {provider.provider_name} (${provider.rate.toFixed(4)}/kWh)
            </option>
          ))}
        </select>
        {state.electricProviders.length === 0 && (
          <div style={{
            fontSize: '13px',
            color: '#f59e0b',
            marginTop: '6px',
            padding: '8px',
            background: '#fffbeb',
            borderRadius: '4px'
          }}>
            ⚠️ No electric providers configured. Please add one in Settings → Utilities.
          </div>
        )}
        {errors.electricProviderId && (
          <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>
            {errors.electricProviderId}
          </div>
        )}
      </div>

      {/* Gas/Propane Checkbox */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          fontWeight: '600',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '2px solid #e0e6ed'
        }}>
          <input
            type="checkbox"
            checked={state.hasGasOrPropane || false}
            onChange={(e) => {
              setField('hasGasOrPropane', e.target.checked);
              // Clear gas provider if unchecked
              if (!e.target.checked) {
                setField('gasProviderId', '');
              }
            }}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span>🔵 Customer has Natural Gas or Propane service</span>
        </label>
      </div>

      {/* Gas/Propane Provider (conditional) */}
      {state.hasGasOrPropane && (
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
            🔵 Natural Gas / Propane Provider <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            className="input"
            value={state.gasProviderId || ''}
            onChange={(e) => setField('gasProviderId', e.target.value)}
            required
          >
            <option value="">-- Select Provider --</option>
            {state.gasProviders.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.provider_name} (${provider.rate.toFixed(4)}/{provider.unit})
              </option>
            ))}
          </select>
          {state.gasProviders.length === 0 && (
            <div style={{
              fontSize: '13px',
              color: '#f59e0b',
              marginTop: '6px',
              padding: '8px',
              background: '#fffbeb',
              borderRadius: '4px'
            }}>
              ⚠️ No gas/propane providers configured. Please add one in Settings → Utilities.
            </div>
          )}
          {errors.gasProviderId && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>
              {errors.gasProviderId}
            </div>
          )}
        </div>
      )}

      {/* Help text */}
      <div style={{
        padding: '12px',
        background: '#f0f9ff',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        💡 <strong>Note:</strong> The selected service area and utility providers will be used
        to calculate accurate energy costs and filter applicable rebates for this property.
      </div>
    </div>
  );
}
